import '../shared-module-config'

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { useEffectOnce } from '@tmap-web-lib/utils/react'
import { AxiosError } from 'axios'
import { Provider, useAtomValue, useSetAtom } from 'jotai'
import { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'

import { GlobalLoadingSpinnerProvider, GlobalModalManagerProvider, InitializationProvider } from '@/Providers'
import PageIdContextProvider from '@/Providers/page-id-context/PageIdContextProvider'
import { SafeAreaProvider } from '@/Providers/safe-area-provider'
import { allServicesPageErrorAtom, errorBoundaryActiveAtom } from '@/atoms'
import { FrameLayout } from '@/components'
import Error from '@/components/Errors/Error'
import { GlobalLoadingSpinnerDisabledPagePath } from '@/routes/global-loading-spinner-disabled-page-path'
import { PortraitOnlyPagePath } from '@/routes/portrait-only-page-path'
import { StatusBarExpandedPagePath } from '@/routes/status-bar-expanded-page-path'
import { StepCounterPagePaths } from '@/routes/step-counter-page-path'
import { applyTmapAppBrowserProxy, hideJsLoadFallback, isBrowser } from '@/utils'

import useMockInitHook from '../hooks/useMockInitHook'

const DevTools = dynamic(
  () => import('@/components/DevTools').then((component) => component.default),
  {
    ssr: false,
  },
)

const devToolEnabled = process.env.NEXT_PUBLIC_DEBUG_VIEW === 'Y'
const FONT_CDN = process.env.NEXT_PUBLIC_FONT_CDN
const MAX_HEALTH_CHECK_ATTEMPTS = 3

if (!TmapApp.env.isInApp && isBrowser()) {
  applyTmapAppBrowserProxy()
}

function HideJsLoadFallback() {
  useEffect(() => {
    hideJsLoadFallback()
  }, [])
  return null
}

function ErrorOffline() {
  const router = useRouter()
  const [offlineError] = useState(() => new globalThis.Error('네트워크 연결이 끊어졌습니다.') as any)
  const sourcePath = typeof window !== 'undefined' ? window.location.pathname : router.pathname

  return (
    <Error
      error={offlineError}
      sourcePath={sourcePath}
    />
  )
}

function InnerApp({ Component, ...rest }: AppProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              const status = (error as AxiosError | undefined)?.response?.status
              // 네트워크 오류(status 없음) 또는 5xx 서버 에러인 경우 1회 재시도
              if (!status || (status >= 500 && status < 600)) {
                return failureCount < 1
              }
              return false
            },
            refetchOnWindowFocus: false,
            cacheTime: 5 * 60 * 1000,
            staleTime: 5 * 60 * 1000,
            useErrorBoundary: true,
          },
        },
        queryCache: new QueryCache({
          onError: (error) => {
            console.log(error)
          },
        }),
      }),
  )
  const router = useRouter()
  const setBoundaryActive = useSetAtom(errorBoundaryActiveAtom)
  const [isOffline, setIsOffline] = useState(false)
  const { isEnabled } = useMockInitHook()
  const isAllServicesError = useAtomValue(allServicesPageErrorAtom)

  function ErrorFallback({ error: caughtError }: FallbackProps) {
    useEffect(() => {
      setBoundaryActive(true)
      // fallback 렌더 시 활성
      return () => {
        setBoundaryActive(false) // 복구 시 비활성
      }
    }, [caughtError])

    const sourcePath = typeof window !== 'undefined' ? window.location.pathname : router.pathname

    return (
      <Error
        error={caughtError as any}
        sourcePath={sourcePath}
      />
    )
  }

  useEffect(() => {
    const CHUNK_ERROR_PATTERNS = [
      /loading chunk/i,
      /chunkloaderror/i,
      /loading css chunk/i,
      /unable to preload css/i,
      /importing a module script failed/i,
      /failed to fetch dynamically imported module/i,
      /error loading dynamically imported module/i,
    ]

    const isChunkError = (error: unknown) => {
      if (!error) return false
      if (typeof error === 'object' && (error as Error).name === 'ChunkLoadError') return true
      const msg = typeof error === 'string' ? error : (error as Error)?.message ?? ''
      if (!msg) return false
      return CHUNK_ERROR_PATTERNS.some((p) => p.test(msg))
    }

    const handleChunkReload = () => {
      const key = 'chunk_reload'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
      }
    }

    const handleRouteError = (err: Error & { cancelled?: boolean }) => {
      if (!err.cancelled && isChunkError(err)) {
        handleChunkReload()
      }
    }

    const handleWindowError = (event: ErrorEvent) => {
      if (isChunkError(event.error ?? event.message)) {
        handleChunkReload()
      }
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkError(event.reason)) {
        handleChunkReload()
      }
    }

    router.events.on('routeChangeError', handleRouteError)
    window.addEventListener('error', handleWindowError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      router.events.off('routeChangeError', handleRouteError)
      window.removeEventListener('error', handleWindowError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [router.events])

  useEffect(() => {
    let offlineTimer = 0
    let offlineCancelToken: symbol = Symbol()
    let abortController: AbortController | null = null

    const handleOffline = () => {
      // Wi-Fi 전환 또는 데이터 전환 시 일시적 offline 이벤트 오탐 방지
      // 500ms 간격으로 최대 MAX_HEALTH_CHECK_ATTEMPTS회 /api/health 재시도 후 에러 화면 표시
      // LTE 라우팅 안정화 시간(500ms~2초 이상)을 감안해 총 유예 시간: 최대 1.5초(500ms × 3회)
      clearTimeout(offlineTimer)
      abortController?.abort()
      abortController = null
      const currentToken = Symbol()
      offlineCancelToken = currentToken
      const tryHealthCheck = (attempts = 0) => {
        offlineTimer = window.setTimeout(() => {
          abortController = new AbortController()
          fetch('/api/health', { method: 'GET', cache: 'no-store', signal: abortController.signal })
            .then(() => {
              if (offlineCancelToken !== currentToken) return
              setIsOffline(false)
            })
            .catch((err: unknown) => {
              if ((err instanceof DOMException && err.name === 'AbortError') || offlineCancelToken !== currentToken) return
              if (attempts + 1 < MAX_HEALTH_CHECK_ATTEMPTS) {
                tryHealthCheck(attempts + 1)
              } else {
                setIsOffline(true)
              }
            })
        }, 500)
      }
      tryHealthCheck()
    }
    const handleOnline = () => {
      offlineCancelToken = Symbol()
      clearTimeout(offlineTimer)
      abortController?.abort()
      abortController = null
      setIsOffline(false)
      TmapApp.setMyTabTopButtonGroupVisibility({ isVisible: true })
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    const connection = (navigator as Navigator & { connection?: EventTarget }).connection
    const handleConnectionChange = () => {
      if (!navigator.onLine) {
        handleOffline()
      } else {
        handleOnline()
      }
    }
    connection?.addEventListener('change', handleConnectionChange)

    return () => {
      clearTimeout(offlineTimer)
      abortController?.abort()
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      connection?.removeEventListener('change', handleConnectionChange)
    }
  }, [])

  useEffectOnce(() => {
    if (!router.isReady) return false
    if (PortraitOnlyPagePath.has(router.pathname)) {
      TmapApp.setOrientation({ isPortrait: true })
    }
  }, [router])

  if (!isEnabled) {
    return null
  }

  const is404 = rest.pageProps?.is404 === true

  return (
    <div data-page-path={router.pathname}>
      <Head>
        <title>마이</title>
        <meta
          name="description"
          content="마이 서비스"
        />
        <meta
          name="apple-mobile-web-app-capable"
          content="yes"
        />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="default"
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, user-scalable=0, minimum-scale=1.0, maximum-scale=1.0, viewport-fit=cover"
        />
        <meta
          httpEquiv="Cache-control"
          content="no-cache,no-store,must-revalidate"
        />
        {FONT_CDN && (
          <link
            rel="preconnect"
            href={FONT_CDN}
            crossOrigin="anonymous"
          />
        )}
        {FONT_CDN && (
          <link
            rel="preload"
            href={`${FONT_CDN}/tmds/assets/font/TMOBI_Regular.woff2`}
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
        )}
        {FONT_CDN && (
          <link
            rel="preload"
            href={`${FONT_CDN}/tmds/assets/font/TMOBI_Medium.woff2`}
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
        )}
        {FONT_CDN && (
          <link
            rel="preload"
            href={`${FONT_CDN}/tmds/assets/font/TMOBI_Bold.woff2`}
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
        )}
        <link
          rel="icon"
          href="/favicon.ico"
        />
      </Head>
      <InitializationProvider>
        <QueryClientProvider client={queryClient}>
          <PageIdContextProvider>
            <SafeAreaProvider disableInit={!StatusBarExpandedPagePath.has(router.pathname)}>
              <GlobalModalManagerProvider>
                {isOffline && !StepCounterPagePaths.has(router.pathname) ? (
                  <ErrorOffline />
                ) : (
                  <ErrorBoundary
                    onError={() => setBoundaryActive(true)}
                    FallbackComponent={ErrorFallback}
                  >
                    <GlobalLoadingSpinnerProvider
                      offsetTop={'FULL'}
                      disabled={GlobalLoadingSpinnerDisabledPagePath.has(router.pathname)}
                    >
                      <HideJsLoadFallback />
                      <FrameLayout>
                        <Component {...rest.pageProps} />
                      </FrameLayout>
                      {/* 메인 화면 Component 내부에서 useErrorBoundary:true로 해둔 API(인증 등)의 에러가 잡히면 children 전체를 날리고, fallback UI(<Error />)를 대신 렌더링.
                    DevTools, ReactQueryDevtools 렌더되지 않아 따로 미노출 처리 필요 없음.
                    전체서비스의 주요 api들은 useErrorBoundary:false지만 따로 에러 팝업 띄우는 처리 하고 있어서 fallback UI(<Error />를 타지 않아 DevTools, ReactQueryDevtools  미노출 처리 필요
                     */}
                      {!is404 && !isOffline && devToolEnabled && !isAllServicesError && <DevTools />}
                      {!is404 && !isOffline && !isAllServicesError && (
                        <ReactQueryDevtools
                          position={'bottom-right'}
                          toggleButtonProps={{
                            style: { width: '26px', height: '26px', margin: 0, alignItems: 'center' },
                          }}
                        />
                      )}
                    </GlobalLoadingSpinnerProvider>
                  </ErrorBoundary>
                )}
              </GlobalModalManagerProvider>
            </SafeAreaProvider>
          </PageIdContextProvider>
        </QueryClientProvider>
      </InitializationProvider>
    </div>
  )
}

export default function AppWithProviders(props: AppProps) {
  return (
    <Provider>
      <InnerApp {...props} />
    </Provider>
  )
}
